import React, { useEffect, useState } from 'react';
import { Exam } from '../../types/exam';
import { examApi } from '../../services/exam.service';
import QuestionManager from './QuestionManager';

interface ExamDetailProps {
  exam: Exam;
  onUpdate: (examId: string, data: any) => Promise<void>;
  onPublish: (examId: string) => Promise<void>;
  onPublishResults: (examId: string) => Promise<void>;
  onDelete: (examId: string) => void;
  onRefresh: () => void;
}

export const ExamDetail: React.FC<ExamDetailProps> = ({
  exam,
  onUpdate,
  onPublish,
  onPublishResults,
  onDelete,
  onRefresh,
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'questions' | 'analytics'>('details');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePublish = async () => {
    if (!window.confirm('Submit this exam for admin approval?')) return;

    try {
      setLoading(true);
      await onPublish(exam.examId);
      setError(null);
    } catch (err) {
      setError('Failed to submit exam for admin approval');
    } finally {
      setLoading(false);
    }
  };

  const handlePublishResults = async () => {
    if (!window.confirm('Publish results, ranks, and eligible certificates for this exam?')) return;

    try {
      setLoading(true);
      await onPublishResults(exam.examId);
      setError(null);
      onRefresh();
    } catch (err) {
      setError('Failed to publish exam results');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      case 'PUBLISHED':
        return 'bg-green-100 text-green-800';
      case 'CLOSED':
        return 'bg-yellow-100 text-yellow-800';
      case 'ARCHIVED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">{exam.title}</h1>
            <p className="text-blue-100">{exam.description}</p>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-semibold bg-white ${getStatusColor(exam.status).split(' ')[1]}`}>
            {exam.status}
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-blue-200">Subject</p>
            <p className="font-semibold">{exam.subject}</p>
          </div>
          <div>
            <p className="text-blue-200">Total Marks</p>
            <p className="font-semibold">{exam.totalMarks}</p>
          </div>
          <div>
            <p className="text-blue-200">Duration</p>
            <p className="font-semibold">{exam.duration} minutes</p>
          </div>
          <div>
            <p className="text-blue-200">Passing %</p>
            <p className="font-semibold">{exam.passingPercentage}%</p>
          </div>
          <div>
            <p className="text-blue-200">Exam Type</p>
            <p className="font-semibold">{(exam.examType || 'GENERAL').replace('_', ' ')}</p>
          </div>
          <div>
            <p className="text-blue-200">Result Mode</p>
            <p className="font-semibold">{exam.resultPublicationMode === 'MANUAL' ? 'Manual' : 'Immediate'}</p>
          </div>
          <div>
            <p className="text-blue-200">Ranks</p>
            <p className="font-semibold">{exam.rankGenerationEnabled === false ? 'Off' : 'On'}</p>
          </div>
          <div>
            <p className="text-blue-200">Certificates</p>
            <p className="font-semibold">{exam.certificatesEnabled === false ? 'Off' : 'Auto'}</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        {exam.status === 'DRAFT' && (
          <>
            <button
              onClick={handlePublish}
              disabled={loading || exam.totalQuestions < 5}
              className="bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white px-4 py-2 rounded font-medium transition"
              title={exam.totalQuestions < 5 ? 'Minimum 5 questions required' : ''}
            >
              Submit For Approval
            </button>
            <p className="text-sm text-gray-600 self-center">
              Questions: {exam.totalQuestions}/5 (minimum required)
            </p>
          </>
        )}
        {exam.status === 'SUBMITTED' && (
          <div className="rounded border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">
            Submitted to admin for approval. You can publish only after admin approval.
          </div>
        )}

        {exam.status === 'PUBLISHED' && (
          <>
            <button
              onClick={() => setActiveTab('analytics')}
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded font-medium transition"
            >
              View Analytics
            </button>
            {exam.resultPublicationMode === 'MANUAL' && !exam.resultsPublished && (
              <button
                onClick={handlePublishResults}
                disabled={loading}
                className="bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-300 text-white px-4 py-2 rounded font-medium transition"
              >
                Publish Results
              </button>
            )}
            {exam.resultPublicationMode === 'MANUAL' && exam.resultsPublished && (
              <div className="rounded border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
                Results published{exam.resultsPublishedAt ? ` on ${formatDate(exam.resultsPublishedAt)}` : ''}.
              </div>
            )}
          </>
        )}

        <button
          onClick={() => onDelete(exam.examId)}
          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded font-medium transition"
        >
          Delete Exam
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('details')}
            className={`pb-2 px-4 font-medium transition ${
              activeTab === 'details'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('questions')}
            className={`pb-2 px-4 font-medium transition ${
              activeTab === 'questions'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Questions ({exam.totalQuestions})
          </button>
          {exam.status === 'PUBLISHED' && (
            <button
              onClick={() => setActiveTab('analytics')}
              className={`pb-2 px-4 font-medium transition ${
                activeTab === 'analytics'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Analytics
            </button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="bg-white rounded-lg p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-800 mb-4">Exam Settings</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Start Date</p>
                  <p className="font-medium text-gray-800">{formatDate(exam.startDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">End Date</p>
                  <p className="font-medium text-gray-800">{formatDate(exam.endDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Duration</p>
                  <p className="font-medium text-gray-800">{exam.duration} minutes</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Exam Type</p>
                  <p className="font-medium text-gray-800">{(exam.examType || 'GENERAL').replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Chapters</p>
                  <p className="font-medium text-gray-800">
                    {exam.chapters?.length ? exam.chapters.join(', ') : 'Not configured'}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 mb-4">Marking Options</h3>
              <div className="space-y-2">
                <div className="text-gray-700">
                  Negative Marking: <span className="font-medium">{exam.negativeMarking || 0}%</span>
                </div>
                <div className="text-gray-700">
                  Result Publication:{' '}
                  <span className="font-medium">
                    {exam.resultPublicationMode === 'MANUAL' ? 'Manual teacher publish' : 'Immediate after submission'}
                  </span>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exam.partialMarking}
                    disabled
                    className="w-4 h-4 mr-2"
                  />
                  <label className="text-gray-700">Partial Marking Enabled</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exam.showAnswersAfterExam}
                    disabled
                    className="w-4 h-4 mr-2"
                  />
                  <label className="text-gray-700">Show Answers After Exam</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exam.rankGenerationEnabled !== false}
                    disabled
                    className="w-4 h-4 mr-2"
                  />
                  <label className="text-gray-700">Rank Generation Enabled</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exam.certificatesEnabled !== false}
                    disabled
                    className="w-4 h-4 mr-2"
                  />
                  <label className="text-gray-700">Certificate Automation Enabled</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exam.shuffleQuestions}
                    disabled
                    className="w-4 h-4 mr-2"
                  />
                  <label className="text-gray-700">Shuffle Questions</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exam.shuffleOptions}
                    disabled
                    className="w-4 h-4 mr-2"
                  />
                  <label className="text-gray-700">Shuffle Options</label>
                </div>
              </div>
            </div>
          </div>

          {exam.status === 'PUBLISHED' && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded mt-6">
              <h4 className="font-semibold text-blue-900 mb-2">Statistics</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-blue-600">Total Attempts</p>
                  <p className="text-2xl font-bold text-blue-900">{exam.totalAttempts}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-600">Average Score</p>
                  <p className="text-2xl font-bold text-blue-900">{exam.averageScore?.toFixed(2) || 0}%</p>
                </div>
                <div>
                  <p className="text-sm text-blue-600">Pass Rate</p>
                  <p className="text-2xl font-bold text-blue-900">{exam.passRate?.toFixed(1) || 0}%</p>
                </div>
                <div>
                  <p className="text-sm text-blue-600">Questions</p>
                  <p className="text-2xl font-bold text-blue-900">{exam.totalQuestions}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'questions' && (
        <QuestionManager
          examId={exam.examId}
          examStatus={exam.status}
          totalQuestions={exam.totalQuestions}
          onRefresh={onRefresh}
        />
      )}

      {activeTab === 'analytics' && exam.status === 'PUBLISHED' && <ExamAnalyticsTab examId={exam.examId} />}
    </div>
  );
};

// Analytics Tab Component
const ExamAnalyticsTab: React.FC<{ examId: string }> = ({ examId }) => {
  const [analytics, setAnalytics] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');

    examApi
      .getExamAnalytics(examId)
      .then((data) => {
        if (mounted) setAnalytics(data);
      })
      .catch((err) => {
        console.error('Failed to load exam analytics', err);
        if (mounted) setError('Failed to load analytics');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [examId]);

  const studentPerformance = analytics?.studentPerformance || analytics?.studentAnalytics || [];
  const questionAnalytics = analytics?.questionAnalytics || [];

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 text-center py-12">
        <p className="text-gray-500">Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Attempts', value: analytics?.totalAttempts || 0 },
          { label: 'Passed', value: analytics?.totalPassed ?? analytics?.passCount ?? 0 },
          { label: 'Failed', value: analytics?.totalFailed ?? analytics?.failCount ?? 0 },
          { label: 'Average Score', value: `${analytics?.averageScore || 0}%` },
        ].map((item) => (
          <div key={item.label} className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-500">{item.label}</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-semibold text-gray-800">Generated Ranks</h3>
          <span className="text-sm text-gray-500">
            {analytics?.rankGenerationEnabled === false ? 'Rank generation disabled' : 'Ranked by percentage, score, time, then submission time'}
          </span>
        </div>
        {studentPerformance.length === 0 ? (
          <p className="text-sm text-gray-500">No graded attempts yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Rank</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Attempt</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Score</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Percentage</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Grade</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {studentPerformance.map((item: any, index: number) => (
                  <tr key={item.attemptId || `${item.studentId}-${index}`}>
                    <td className="px-3 py-2 font-semibold text-gray-900">{item.rank || '-'}</td>
                    <td className="px-3 py-2 text-gray-600">{item.attemptId || item.studentId || '-'}</td>
                    <td className="px-3 py-2 text-gray-600">{item.score}</td>
                    <td className="px-3 py-2 text-gray-600">{item.percentage ?? item.percentageScore}%</td>
                    <td className="px-3 py-2 text-gray-600">{item.grade || '-'}</td>
                    <td className="px-3 py-2 text-gray-600">{item.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4">Question Bank Performance</h3>
        {questionAnalytics.length === 0 ? (
          <p className="text-sm text-gray-500">Question analytics will appear after attempts are graded.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {questionAnalytics.map((question: any) => (
              <div key={question.questionId} className="rounded-lg border border-gray-200 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-gray-500">Q{question.sequenceNumber || '-'}</span>
                  {question.chapter && (
                    <span className="rounded bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                      {question.chapter}
                    </span>
                  )}
                  <span className="rounded bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">
                    {question.questionType || 'Question'}
                  </span>
                  <span className="rounded bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">
                    {question.marks || 0}M
                  </span>
                  {Number(question.negativeMarks || 0) > 0 && (
                    <span className="rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">
                      -{question.negativeMarks}M
                    </span>
                  )}
                </div>
                <p className="mt-3 text-sm text-gray-800">{question.questionText || question.questionId}</p>
                <p className="mt-2 text-xs text-gray-500">
                  Attempts: {question.attempts ?? question.totalAttempts ?? 0} | Correct:{' '}
                  {question.correctAttempts ?? 0} | Accuracy:{' '}
                  {question.correctPercentage ?? question.accuracy ?? 0}%
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamDetail;
